import type { CameraViewPreset, LayoutElement } from '../types';
import { REFERENCE_CAMERA_PRESETS } from './fabVisualConfig';
import { elementUses3DElevation, getElement3DExtrusionDepth, getElement3DGroupY } from './layout3d';

export interface CameraViewPose {
  position: [number, number, number];
  target: [number, number, number];
  fov: number;
  minPolarAngle?: number;
  maxPolarAngle?: number;
}

const DEFAULT_FOV = 40;

/** Look at mid-fab height so rails and floor tools share the frame. */
export function getLayoutCameraLookHeight(railElevation: number): number {
  return Math.max(10, railElevation * 0.42);
}

export function compareOhtViewNames(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export function sortOhtElements<T extends { metadata?: { name?: string } }>(items: T[]): T[] {
  return [...items].sort((left, right) =>
    compareOhtViewNames(left.metadata?.name || '', right.metadata?.name || '')
  );
}

/** Prefer an explicit chase target, then the selected OHT, then the first sorted vehicle. */
export function resolveFollowOhtId(
  ohtIdsSorted: string[],
  selectedId: string | null | undefined,
  isOht: (id: string) => boolean,
  preferredId?: string | null
): string | null {
  if (preferredId && isOht(preferredId)) return preferredId;
  if (selectedId && isOht(selectedId)) return selectedId;
  return ohtIdsSorted[0] ?? null;
}

export function getElementFocusCenter(
  element: LayoutElement,
  railElevation: number,
  scale: number,
  origin: { cx: number; cz: number }
): { x: number; y: number; z: number; span: number } {
  const w = element.dimensions.width;
  const h = element.dimensions.height;
  const rad = ((element.rotation || 0) * Math.PI) / 180;
  const elevated = elementUses3DElevation(element.type);
  const localX = elevated ? 0 : w / 2;
  const localY = elevated ? 0 : h / 2;
  const worldX = element.position.x + localX * Math.cos(rad) - localY * Math.sin(rad);
  const worldZ = element.position.y + localX * Math.sin(rad) + localY * Math.cos(rad);
  return {
    x: worldX * scale - origin.cx,
    z: worldZ * scale - origin.cz,
    y: getElement3DGroupY(element, undefined, railElevation) * scale,
    span: Math.max(w, h, getElement3DExtrusionDepth(element), 24) * scale,
  };
}

export function getStandardViewPose(
  preset: Exclude<CameraViewPreset, 'follow' | 'focus'>,
  args: { cx: number; cz: number; lookY: number; spanX: number; spanZ: number }
): CameraViewPose {
  const { cx, cz, lookY } = args;
  const spanX = Math.max(args.spanX, 200);
  const spanZ = Math.max(args.spanZ, 200);
  const span = Math.max(spanX, spanZ);

  if (preset === 'reference' || preset === 'front45' || preset === 'wide') {
    const ref =
      preset === 'reference'
        ? REFERENCE_CAMERA_PRESETS.REFERENCE_VIEW
        : preset === 'front45'
          ? REFERENCE_CAMERA_PRESETS.FRONT_45
          : REFERENCE_CAMERA_PRESETS.WIDE_FAB;
    const dist = span * ref.distanceScale;
    const [ox, oy, oz] = ref.offset;
    return {
      position: [cx + dist * ox, lookY + dist * oy, cz + dist * oz],
      target: [cx, lookY, cz],
      fov: ref.fov,
    };
  }

  if (preset === 'top') {
    const dist = span * 1.08;
    return {
      position: [cx, dist, cz + Math.max(0.8, span * 0.0003)],
      target: [cx, 0, cz],
      fov: 42,
      minPolarAngle: 0.001,
      maxPolarAngle: 0.35,
    };
  }

  if (preset === 'front' || preset === 'back') {
    const dist = Math.max(spanX * 0.72, spanZ * 0.18, lookY * 2.1, 160);
    const sign = preset === 'front' ? 1 : -1;
    return {
      position: [cx, lookY + dist * 0.16, cz + sign * dist],
      target: [cx, lookY, cz],
      fov: 38,
    };
  }

  if (preset === 'left' || preset === 'right') {
    const dist = Math.max(spanZ * 0.72, spanX * 0.18, lookY * 2.1, 160);
    const sign = preset === 'right' ? 1 : -1;
    return {
      position: [cx + sign * dist, lookY + dist * 0.16, cz],
      target: [cx, lookY, cz],
      fov: 38,
    };
  }

  const dist = span * 0.92;
  return {
    position: [cx + dist * 0.58, lookY + dist * 0.52, cz + dist * 0.58],
    target: [cx, lookY, cz],
    fov: DEFAULT_FOV,
  };
}

export function getFocusViewPose(center: {
  x: number;
  y: number;
  z: number;
  span: number;
}): CameraViewPose {
  const dist = Math.max(center.span * 2.1, 90);
  return {
    position: [center.x + dist * 0.72, center.y + dist * 0.48, center.z + dist * 0.72],
    target: [center.x, center.y + center.span * 0.18, center.z],
    fov: 36,
  };
}
